import type { InterviewSummary } from "@/api/generated/types";
import { type InterviewDisplayStatus, mapInterviewStatusToDisplay } from "@/lib/interview-ui";
import type { CandidateStatusTab } from "../_components/candidate-status-tabs";

export type SortKey = "name" | "status" | "score" | "createdAt";
export type SortDirection = "asc" | "desc";
export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

export const DEFAULT_SORT: SortState = { key: "createdAt", direction: "desc" };

const INITIAL_DIRECTION: Record<SortKey, SortDirection> = {
  name: "asc",
  status: "asc",
  score: "desc",
  createdAt: "desc",
};

const STATUS_SORT_RANK: Record<InterviewDisplayStatus, number> = {
  新規応募: 0,
  未対応: 1,
  面談中: 2,
  評価中: 3,
  面談完了: 4,
  評価失敗: 5,
  中断: 6,
};

export function nextSortState(current: SortState, key: SortKey): SortState {
  if (current.key === key) {
    return { key, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key, direction: INITIAL_DIRECTION[key] };
}

function compareCreatedAtDesc(a: InterviewSummary, b: InterviewSummary): number {
  const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  return diff !== 0 ? diff : a.interviewId.localeCompare(b.interviewId);
}

function compareWithMissingLast<T>(
  a: InterviewSummary,
  b: InterviewSummary,
  values: [T | null, T | null],
  compare: (x: T, y: T) => number,
  sign: number
): number {
  const [aValue, bValue] = values;
  if (aValue === null || bValue === null) {
    if (aValue === null && bValue === null) return compareCreatedAtDesc(a, b);
    return aValue === null ? 1 : -1;
  }
  const diff = compare(aValue, bValue);
  return diff !== 0 ? diff * sign : compareCreatedAtDesc(a, b);
}

function toSortableName(name: string | null): string | null {
  const trimmed = name?.trim() ?? "";
  return trimmed.length === 0 ? null : trimmed;
}

function toStatusRank(interview: InterviewSummary, now: Date): number {
  return STATUS_SORT_RANK[mapInterviewStatusToDisplay(interview.status, interview.createdAt, now)];
}

const subtract = (x: number, y: number) => x - y;

const SORT_COMPARATORS: Record<
  SortKey,
  (a: InterviewSummary, b: InterviewSummary, sign: number, now: Date) => number
> = {
  createdAt: (a, b, sign) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return diff !== 0 ? diff * sign : a.interviewId.localeCompare(b.interviewId);
  },
  score: (a, b, sign) =>
    compareWithMissingLast(a, b, [a.overallScore, b.overallScore], subtract, sign),
  name: (a, b, sign) =>
    compareWithMissingLast(
      a,
      b,
      [toSortableName(a.ownerName), toSortableName(b.ownerName)],
      (x, y) => x.localeCompare(y, "ja"),
      sign
    ),
  status: (a, b, sign, now) =>
    compareWithMissingLast(a, b, [toStatusRank(a, now), toStatusRank(b, now)], subtract, sign),
};

export function sortInterviews(
  interviews: InterviewSummary[],
  sort: SortState,
  now: Date
): InterviewSummary[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  const compare = SORT_COMPARATORS[sort.key];
  return [...interviews].sort((a, b) => compare(a, b, sign, now));
}

export function findTopScoreInterviewId(interviews: InterviewSummary[]): string | undefined {
  return sortInterviews(interviews, { key: "score", direction: "desc" }, new Date()).find(
    (interview) => interview.overallScore !== null
  )?.interviewId;
}

export type ScoreFilter = "all" | "high" | "middle" | "low" | "unscored";
export type AppliedDateFilter = "all" | "today" | "7d" | "30d" | "90d";

export const SCORE_FILTER_HIGH_MIN = 76;
export const SCORE_FILTER_MIDDLE_MIN = 64;
const DAY_MS = 24 * 60 * 60 * 1000;
const APPLIED_DATE_WINDOW_DAYS: Record<Exclude<AppliedDateFilter, "all" | "today">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export const SCORE_FILTER_OPTIONS: { value: ScoreFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "high", label: `${SCORE_FILTER_HIGH_MIN}点以上（S・A）` },
  { value: "middle", label: `${SCORE_FILTER_MIDDLE_MIN}〜${SCORE_FILTER_HIGH_MIN - 1}点（B）` },
  { value: "low", label: `${SCORE_FILTER_MIDDLE_MIN - 1}点以下（C・D）` },
  { value: "unscored", label: "未評価" },
];

export const APPLIED_DATE_FILTER_OPTIONS: { value: AppliedDateFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "today", label: "今日" },
  { value: "7d", label: "過去7日間" },
  { value: "30d", label: "過去30日間" },
  { value: "90d", label: "過去90日間" },
];

export interface CandidateListCriteria {
  search: string;
  tab: CandidateStatusTab;
  score: ScoreFilter;
  appliedDate: AppliedDateFilter;
}

export function matchesScore(score: number | null, filter: ScoreFilter): boolean {
  if (filter === "all") return true;
  if (filter === "unscored") return score === null;
  if (score === null) return false;
  if (filter === "high") return score >= SCORE_FILTER_HIGH_MIN;
  if (filter === "middle") return score >= SCORE_FILTER_MIDDLE_MIN && score < SCORE_FILTER_HIGH_MIN;
  return score < SCORE_FILTER_MIDDLE_MIN;
}

export function matchesAppliedDate(
  createdAt: string,
  filter: AppliedDateFilter,
  now: Date
): boolean {
  if (filter === "all") return true;
  const created = new Date(createdAt).getTime();
  if (filter === "today") {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return created >= startOfToday;
  }
  return created >= now.getTime() - APPLIED_DATE_WINDOW_DAYS[filter] * DAY_MS;
}

export function matchesSearch(interview: InterviewSummary, query: string): boolean {
  if (query.length === 0) {
    return true;
  }
  const haystack = [
    interview.ownerName ?? "",
    interview.ownerEmail ?? "",
    interview.templateTitle ?? "",
    interview.templateSlug ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

export function toStatusTab(display: InterviewDisplayStatus): Exclude<CandidateStatusTab, "all"> {
  if (display === "新規応募" || display === "未対応") return "未対応";
  if (display === "面談中" || display === "評価中") return "進行中";
  if (display === "面談完了") return "面談完了";
  return "中断・失敗";
}

function matchesBaseCriteria(
  interview: InterviewSummary,
  criteria: Omit<CandidateListCriteria, "tab">,
  now: Date
): boolean {
  return (
    matchesSearch(interview, criteria.search.trim().toLowerCase()) &&
    matchesScore(interview.overallScore, criteria.score) &&
    matchesAppliedDate(interview.createdAt, criteria.appliedDate, now)
  );
}

export function filterInterviews(
  interviews: InterviewSummary[],
  criteria: CandidateListCriteria,
  now: Date
): InterviewSummary[] {
  return interviews.filter(
    (interview) =>
      matchesBaseCriteria(interview, criteria, now) &&
      (criteria.tab === "all" ||
        toStatusTab(mapInterviewStatusToDisplay(interview.status, interview.createdAt, now)) ===
          criteria.tab)
  );
}

export function computeTabCounts(
  interviews: InterviewSummary[],
  criteria: Omit<CandidateListCriteria, "tab">,
  now: Date
): Record<CandidateStatusTab, number> {
  const counts: Record<CandidateStatusTab, number> = {
    all: 0,
    未対応: 0,
    進行中: 0,
    面談完了: 0,
    中断・失敗: 0,
  };
  for (const interview of interviews) {
    if (!matchesBaseCriteria(interview, criteria, now)) continue;
    counts.all += 1;
    counts[toStatusTab(mapInterviewStatusToDisplay(interview.status, interview.createdAt, now))] +=
      1;
  }
  return counts;
}
